import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { DocumentTextIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import client from '../../lib/apolloClient';

const UPLOAD_ENROLLMENT_FILE = gql`
  mutation UploadEnrollmentFile($sprint_enrollment_id: ID!, $file: File!, $name: String, $category: String) {
    upload_enrollment_file(sprint_enrollment_id: $sprint_enrollment_id, file: $file, name: $name, category: $category) {
      document {
        id
        name
        download_url
        created_at
        source_type
        can_delete
        uploaded_by {
          name
        }
      }
      errors
    }
  }
`;

const DELETE_ENROLLMENT_FILE = gql`
  mutation DeleteEnrollmentFile($document_id: ID!, $sprint_enrollment_id: ID!) {
    delete_enrollment_file(document_id: $document_id, sprint_enrollment_id: $sprint_enrollment_id) {
      success
      errors
    }
  }
`;

const CREATE_DOCUMENT = gql`
  mutation CreateDocument($file: File!, $name: String, $category: String, $document_type: Int) {
    createDocument(file: $file, name: $name, category: $category, document_type: $document_type) {
      id
      name
      download_url
      created_at
    }
  }
`;

const EnrollmentFilesTab = ({ enrollment, onRefetch }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Capture the input element before async operations (React reuses synthetic events)
    const inputElement = e.target;

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`File size exceeds 10MB limit. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const result = await client.mutate({
        mutation: UPLOAD_ENROLLMENT_FILE,
        variables: {
          sprint_enrollment_id: enrollment.id,
          file: [file],
          name: file.name,
          category: 'other',
        },
        context: { hasUpload: true },
      });

      if (result.data?.upload_enrollment_file?.errors?.length > 0) {
        throw new Error(result.data.upload_enrollment_file.errors.join(', '));
      }

      // Reset file input using captured element
      if (inputElement) {
        inputElement.value = '';
      }

      // Refetch enrollment data
      if (onRefetch) {
        onRefetch();
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      setDeletingId(documentId);
      setError(null);

      const result = await client.mutate({
        mutation: DELETE_ENROLLMENT_FILE,
        variables: {
          document_id: documentId,
          sprint_enrollment_id: enrollment.id,
        },
      });

      if (result.data?.delete_enrollment_file?.errors?.length > 0) {
        throw new Error(result.data.delete_enrollment_file.errors.join(', '));
      }

      // Refetch enrollment data
      if (onRefetch) {
        onRefetch();
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err.message || 'Failed to delete file');
    } finally {
      setDeletingId(null);
    }
  };

  // Group files by source type
  const files = enrollment?.allFiles || [];
  const programResources = files.filter(f => f.source_type === 'sprint_program');
  const enrollmentFiles = files.filter(f => f.source_type === 'sprint_enrollment');
  const taskDocuments = files.filter(f => f.source_type === 'task');

  const FileItem = ({ file }) => (
    <div className="flex items-center justify-between p-3 bg-succession-bg-secondary rounded-lg border border-succession-border hover:bg-succession-hover-bg transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <DocumentTextIcon className="w-5 h-5 text-succession-text-secondary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-succession-text-primary truncate">
            {file.name || 'Unnamed file'}
          </div>
          <div className="text-xs text-succession-text-secondary">
            {file.uploaded_by?.name && `Uploaded by ${file.uploaded_by.name}`}
            {file.created_at && ` • ${formatDate(file.created_at)}`}
            {file.file_size && ` • ${formatFileSize(file.file_size)}`}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {file.download_url && (
          <a
            href={file.download_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-succession-text-secondary hover:text-succession-primary transition-colors"
            title="Download"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </a>
        )}
        {file.can_delete && (
          <button
            onClick={() => handleDelete(file.id)}
            disabled={deletingId === file.id}
            className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Delete"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950 p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Upload Section */}
      <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-4">
        <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-succession-border rounded-lg cursor-pointer text-succession-text-primary hover:bg-succession-hover-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <DocumentTextIcon className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Upload File'}
          <input
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </label>
        <p className="text-xs text-succession-text-secondary mt-2">
          Maximum file size: 10MB
        </p>
      </div>

      {/* Program Resources Section */}
      {programResources.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-succession-text-primary mb-3">
            Program Resources
          </h3>
          <div className="space-y-2">
            {programResources.map((file) => (
              <FileItem key={file.id} file={file} />
            ))}
          </div>
        </div>
      )}

      {/* Enrollment Files Section */}
      {enrollmentFiles.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-succession-text-primary mb-3">
            Enrollment Files
          </h3>
          <div className="space-y-2">
            {enrollmentFiles.map((file) => (
              <FileItem key={file.id} file={file} />
            ))}
          </div>
        </div>
      )}

      {/* Task Documents Section */}
      {taskDocuments.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-succession-text-primary mb-3">
            Task Documents
          </h3>
          <div className="space-y-2">
            {taskDocuments.map((file) => (
              <FileItem key={file.id} file={file} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {files.length === 0 && (
        <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-12 text-center">
          <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 text-succession-text-secondary opacity-50" />
          <p className="text-sm text-succession-text-secondary">
            No files uploaded yet. Upload your first file above.
          </p>
        </div>
      )}
    </div>
  );
};

export default EnrollmentFilesTab;

